import { Inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { UserParametersService } from 'projects/box-lib/src/lib/services/user-parameters.service';
import { BehaviorSubject } from 'rxjs';
import { UserInfo } from '../models/UserInfo';
import {
  BackOfficeMember,
  Consultant,
  ConsultantHabilitation,
  Notification,
  Operation,
  OperationType,
  Produit,
  UserInformations,
} from '../models/generated/graphql';
import { filterHabilitations } from '../utils/habilitation';

const notifyLogin = gql`
  mutation notifyUserLogin {
    notifyUserLogin {
      notifications {
        id
        message
        isActive
        consultantId
        backOfficeMemberId
        link
        linkLabel
      }
      backOfficeMember {
        id
        tokenId
        displayName
        groupeGestionnaire
        isAdmin
        isActive
        withGroupeAccessExtended
        serviceAllowedEnvelopeIds
      }
    }
  }
`;

const deleteNotificationByLink = gql`
  mutation deleteNotificationsByLink($link: String!) {
    deleteNotificationsByLink(link: $link)
  }
`;

const consultantUser = gql`
  query consultantByCode($code: String!) {
    consultantByCode(code: $code) {
      id
      code
      consultantHabilitations {
        id
        habilitationId
        dateDebut
        dateFin
        consultantId
        codeHabilitation
        statut
        habilitation {
          id
          code
          libelle
        }
      }
    }
  }
`;
const CHECK_HABILIATIONS_CODES = ['ASSU', 'CIF', 'ALPSI']; // List of habilitations codes to check for the consultant

// Class handling the login process, token decoding, and user information retrieval
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  static token_sessionstorage_key = 'token';
  userInfo: any;
  isAuthenticatedValue = false;
  isBackOfficeValue = false;
  // isAdminValue = false;
  isAdminSubject = new BehaviorSubject<boolean>(false);
  environment: any;
  public readonly userBackOfficeSubject = new BehaviorSubject<BackOfficeMember | undefined>(undefined);
  public readonly userNotificationsSubject = new BehaviorSubject<Notification[]>([]);
  private userConsultantValidHabilitations: ConsultantHabilitation[] = [];
  private userConsultantExpiredHabilitations: ConsultantHabilitation[] = [];
  // consultantCheck: boolean;

  userHabilitationCheck: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isInitializingLoginValue: boolean = true;
  constructor(
    private apollo: Apollo,
    @Inject('environment')
    environment: any,
    private userParametersService: UserParametersService
  ) {
    this.environment = environment;
  }

  async processToken() {
    // parse token && check authent
    this.parseToken();

    if (this.isAuthenticatedValue) {
      // after successful login, we notify the backend this use is connected. (no argument required, only token is used to recognize the user)
      this.apollo
        .mutate<{ notifyUserLogin: UserInformations }>({
          mutation: notifyLogin,
        })
        .subscribe(result => {
          this.userBackOfficeSubject.next(result.data?.notifyUserLogin?.backOfficeMember ?? undefined);
          this.userNotificationsSubject.next(result.data?.notifyUserLogin?.notifications ?? []);
        });

      if (!this.isBackOfficeValue) {
        this.getConsultantHabilitations();
      } else {
        this.userHabilitationCheck.next(true);
      }
    }
  }

  async deleteNotificationByLink(link: string | undefined) {
    if (!link) {
      return;
    }
    this.apollo
      .mutate<{ deleteNotificationsByLink: boolean }>({
        mutation: deleteNotificationByLink,
        variables: {
          link,
        },
      })
      .subscribe(result => {
        if (result.data?.deleteNotificationsByLink) {
          this.userNotificationsSubject.next(
            this.userNotificationsSubject.value.filter(notification => notification.link !== link)
          );
        }
      });
  }

  redirectToLogin() {
    const loginUrl = this.environment.authConfig.loginUrl;
    const clientId = this.environment.authConfig.clientId;
    const redirectUri = encodeURIComponent(window.location.href);
    window.location.replace(`${loginUrl}?response_type=token&client_id=${clientId}&redirect_uri=${redirectUri}`);
  }

  // Initiates the login process by checking if the access token is present in the URL hash.
  // If found, it stores the token in the session storage and parses it to extract user information.
  // If not found, it redirects the user to the authentication endpoint to obtain the access token.
  async login() {
    // if the access token is present in the URL hash we save it in the session storage and parse it
    const match = window.location.hash.match(/#access_token=(.*?)&/);
    if (match) {
      sessionStorage.setItem(AuthService.token_sessionstorage_key, match[1]);

      this.processToken();

      window.location.hash = '';
    } else {
      // if the access token is not present in the URL hash, At first we check if there is a token in the session storage
      const storedToken = sessionStorage.getItem(AuthService.token_sessionstorage_key);
      if (storedToken && this.isTokenExpired(storedToken)) {
        // an expired token is useless : the API would reject every request, so we go back to the authentication endpoint
        sessionStorage.removeItem(AuthService.token_sessionstorage_key);
      } else if (storedToken) {
        //there is a token in the session storage, we process it
        await this.processToken();

        return;
      }

      //if there is no token in the session storage we redirect the user to the authentication endpoint
      // before redirect we clean local storage to clear user info
      this.redirectToLogin();
    }
  }

  // Decodes a base64-encoded string to unicode
  b64DecodeUnicode(text: string) {
    return decodeURIComponent(
      Array.prototype.map.call(atob(text), c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
  }

  // Decodes the JWT token payload by replacing characters and decoding the Base64 payload
  jwtDecode(tokenStringPayload: string) {
    return JSON.parse(this.b64DecodeUnicode(tokenStringPayload.replace('-', '+').replace('_', '/')));
  }

  // Returns true if the JWT "exp" claim (seconds since epoch) is in the past. A token without readable "exp" is kept.
  private isTokenExpired(token: string): boolean {
    try {
      const payload = token.split('.')[1];
      const exp = payload ? this.jwtDecode(payload)?.exp : undefined;
      return typeof exp === 'number' && exp * 1000 <= Date.now();
    } catch {
      return false;
    }
  }

  // Parses the JWT token stored in the session storage and extracts the user information
  private parseToken() {
    const token = sessionStorage.getItem(AuthService.token_sessionstorage_key);
    const payload = token?.split('.')[1];
    if (!payload) {
      throw new Error('Cannot parse JWT');
    }

    this.userInfo = this.jwtDecode(payload);

    this.userParametersService.checkLocalStorageClear(this.userInfo?.authenticationDate);

    this.checkAuthent();
  }

  // Retrieves the JWT token from the session storage
  getToken(): string {
    const token = sessionStorage.getItem(AuthService.token_sessionstorage_key);
    if (token) {
      return token;
    } else {
      throw new Error('Not logged in');
    }
  }

  // Retrieves a specific code from the session storage
  getCode(): string {
    const code = sessionStorage.getItem('code');
    if (code) {
      return code;
    } else {
      throw new Error('Not logged in');
    }
  }

  // Retrieves the user information extracted from the JWT token.
  getUserInfo(): UserInfo {
    return {
      givenName: this.userInfo?.givenName,
      lastName: this.userInfo?.lastName,
      fullName: this.userInfo?.givenName + ' ' + (this.userInfo?.lastName ?? this.userInfo?.sn ?? ''),
      email: this.userInfo?.sub,
      tokenId: this.userInfo?.id,
      //consultantCode: this.userInfo?.consultantId
    };
  }

  isAdminRole(): boolean {
    return this.isBackOffice() && this.isAdminSubject?.getValue();
  }

  isInitLogin(): boolean {
    return this.isInitializingLoginValue;
  }

  isBackOffice(): boolean {
    return this.isBackOfficeValue;
  }

  getBackOfficeMember(): BackOfficeMember | undefined {
    return this.userBackOfficeSubject?.getValue() ?? undefined;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedValue;
  }

  checkAuthent() {
    const backOfficeGroups = this.environment?.authConfig?.backOfficeGroups ?? [];
    const consultantGroups = this.environment?.authConfig?.consultantGroups ?? [];
    const backOfficeAdminGroups = this.environment?.authConfig?.backOfficeAdminGroups ?? [];

    this.isBackOfficeValue =
      (this.userInfo?.memberOf?.filter(
        (value: string) => backOfficeGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
      )?.length || 0) > 0;
    let isAdminValue =
      this.userInfo?.memberOf?.filter(
        (value: string) =>
          backOfficeAdminGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
      )?.length > 0 || false;
    this.isAdminSubject.next(isAdminValue);
    if (!this.isBackOfficeValue) {
      this.isAuthenticatedValue =
        (this.userInfo?.memberOf?.filter(
          (value: string) => consultantGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
        )?.length ?? 0) > 0;
    } else {
      this.isAuthenticatedValue = true;
    }
    this.isInitializingLoginValue = false;
  }

  /**
   * Retrieve the consultant habilitations from the graphql server.
   * The retrieved habilitations are filtered to only keep the ones with a valid code (one of ASSU, CIF, ALPSI).
   * The valid and expired habilitations are then stored in the userConsultantValidHabilitations and userConsultantExpiredHabilitations arrays.
   * Finally, the userHabilitationCheck subject is updated to true.
   */
  getConsultantHabilitations() {
    this.apollo
      .query<{ consultantByCode: Consultant }>({
        query: consultantUser,
        variables: {
          code: this.userInfo?.id,
        },
      })
      .subscribe(result => {
        const userConsultant = result.data?.consultantByCode;
        if (userConsultant && userConsultant.consultantHabilitations) {
          const habs = filterHabilitations(userConsultant.consultantHabilitations);
          this.userConsultantValidHabilitations = habs.valid.filter(
            hab => hab.codeHabilitation && CHECK_HABILIATIONS_CODES.includes(hab.codeHabilitation)
          );

          this.userConsultantExpiredHabilitations = habs.expired.filter(
            hab => hab.codeHabilitation && CHECK_HABILIATIONS_CODES.includes(hab.codeHabilitation)
          );
          this.userHabilitationCheck.next(true);
        }
      });
  }
  logout(): void {
    sessionStorage.removeItem(AuthService.token_sessionstorage_key);
    window.location.href = this.environment.authConfig.logoutUrl;
  }

  getUserValidHabilitations(): ConsultantHabilitation[] {
    return this.userConsultantValidHabilitations;
  }

  getUserExpiredHabilitations(): ConsultantHabilitation[] {
    return this.userConsultantExpiredHabilitations;
  }

  /**
   * Return the list of valid habilitation ids for the current user. No special cases.
   * @returns {number[]} The list of valid habilitation ids.
   */

  getUserValidHabilitationIds(): number[] {
    if (!this.userConsultantValidHabilitations) {
      return [];
    }
    return this.userConsultantValidHabilitations.map(hab => hab.habilitationId);
  }

  /**
   * Return the list of valid habilitation ids extended with special cases where one habilitation implies another one.
   * For example, if a user has the ALPSI habilitation, it will return CIF habilitation also.
   * @returns {number[]} the list of valid habilitation ids
   */
  getActeDeGestionUserValidHabilitationIds(): number[] {
    return this.extraHabilitationRulesForActeGestion(this.getUserValidHabilitationIds());
  }

  /**
   * Special case for ALPSI and CIF.
   * Extends the list of valid habilitation IDs by adding dependent IDs.
   * If the list contains the CIF_CODE, it adds the ALPSI_CODE, and vice versa.
   *
   * @param validIds - Array of current valid habilitation IDs.
   * @returns Updated array with extended habilitation rules applied.
   */
  private extraHabilitationRulesForActeGestion(validIds: number[]): number[] {
    const ALPSI_CODE = 47;
    const CIF_CODE = 46;

    if (validIds.includes(CIF_CODE)) {
      validIds.push(ALPSI_CODE);
      return validIds;
    }

    if (validIds.includes(ALPSI_CODE)) {
      validIds.push(CIF_CODE);
      return validIds;
    }
    return validIds;
  }
  isAnyHabilitationValid(): boolean {
    return this.userConsultantValidHabilitations?.length > 0;
  }

  /**
   * Checks if the user is habilited for the given operation.
   * @param operation The operation to check.
   * @returns True if the user is habilited for the operation, false otherwise.
   * If the operation does not have a produit, it will return true.
   * If the operation is an ActeDeGestion, it will check against the extended habilitation rules.
   * Otherwise, it will check against the regular habilitation rules.
   */
  isUserHabilitedOperation(operation: Operation) {
    if (!operation?.produit) {
      return true;
    }
    if (operation.operationType === OperationType.ActeDeGestion) {
      return this.isUserHabilitedActeGestion(operation.produit);
    }

    return this.isUserHabilitedSouscription(operation.produit);
  }

  isUserHabilitedActeGestion(produit: Produit) {
    return this.getActeDeGestionUserValidHabilitationIds().includes(
      produit?.habilitation ?? produit?.habilitationNavigation?.id
    );
  }

  isUserHabilitedSouscription(produit: Produit) {
    return this.getUserValidHabilitationIds().includes(produit?.habilitation ?? produit?.habilitationNavigation?.id);
  }

  resetAuthent() {
    this.isAuthenticatedValue = false;
    this.isBackOfficeValue = false;
    this.isAdminSubject.next(false);
    this.userConsultantValidHabilitations = [];
    this.userConsultantExpiredHabilitations = [];
    this.userHabilitationCheck.next(false);
    this.userBackOfficeSubject.next(undefined);

    sessionStorage.removeItem(AuthService.token_sessionstorage_key);
    this.login();
  }
}
